import type { Recipe, RecipeId, RevisionBackend, RevisionSummary } from '@platewell/core';
import type { RevisionSummaryDto } from '@platewell/schema';

/**
 * Revision history read over HTTP.
 *
 * History is derived server-side during projection rather than kept in the CRDT, which is
 * what allows the document to stay garbage-collected and small. The trade-off is that this
 * is the one part of the app that genuinely does not work offline, and the UI has to say so
 * rather than showing an empty list.
 */
export function createHttpRevisionBackend(
  apiBaseUrl: string,
  householdId: string,
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
): RevisionBackend {
  const base = `${apiBaseUrl}/api/households/${householdId}/recipes`;

  return {
    async list(recipeId: RecipeId): Promise<RevisionSummary[]> {
      const response = await fetchImpl(`${base}/${recipeId}/revisions`, { credentials: 'include' });
      if (!response.ok) throw new Error(`failed to load revisions (${response.status})`);
      const body = (await response.json()) as { revisions: RevisionSummaryDto[] };
      return body.revisions.map((row) => ({
        revisionNo: row.revisionNo,
        changedAt: row.changedAt,
        changedBy: row.changedBy,
        changedFields: row.changedFields,
        rebuilt: row.rebuilt,
      }));
    },

    async get(recipeId: RecipeId, revisionNo: number): Promise<Recipe> {
      const response = await fetchImpl(`${base}/${recipeId}/revisions/${revisionNo}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`failed to load revision ${revisionNo}`);
      return (await response.json()) as Recipe;
    },
  };
}
